import { useSearchParams } from 'react-router';
import { useHydrated } from 'remix-utils';
import type { ButtonProps } from '@archipro-design/aria';
import {
    Button,
    Flex,
    pxToRem,
    useAppDisplayModeContext,
    useStyles,
} from '@archipro-design/aria';
import {
    Download2Line,
    HeartFill,
    HeartLine,
    PinterestFill,
    PriceTag3Line,
} from '@archipro-design/icons';
import { useTracker } from '@archipro-website/tracker';
import { HoverAction } from '../../common/hover-action/HoverAction';
import * as S from './HoverButtonsImage.style';
import { useProductTagging } from '~/modules/product-tagging/context';
import { usePermissions } from '@modules/user';
import { BOARD_TERMS } from '~/config/mood-board-terminology';
import { PermissionEnum } from 'generated/graphql';
import { FollowSelector } from '~/modules/moodboards/components';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { buildPinterestShareUrl } from '~/modules/page-builder/util/build-pinterest-share-url';
import { useProfessionalData } from '~/modules/professional/hook/use-professional-data';

const HOVER_BUTTON_PADDING = 12;
const HOVER_BUTTON_PADDING_MOBILE = 8;
const HOVER_BUTTON_BORDER_RADIUS = '50%';

interface HoverButtonsImageProps {
    photoID: number;
    libraryItemId?: number;
    imageURL: string;
    downloadLink?: string;
    taggingEnabled: boolean;
}
const encodeDownloadLink = (link: string) => {
    return link.replaceAll('+', '%2B');
};

const HoverButtonsImage = ({
    photoID,
    libraryItemId,
    imageURL,
    downloadLink,
    taggingEnabled,
}: HoverButtonsImageProps) => {
    const hasMarketingView = usePermissions(PermissionEnum.BmMarketingView);
    const isHydrated = useHydrated();

    const { dispatch: dispatchProductTagging } = useProductTagging();
    const [searchParams, setSearchParams] = useSearchParams();

    const { css } = useStyles();
    const { mobile: isMobile } = useAppDisplayModeContext();
    const tracker = useTracker();
    const professionalData = useProfessionalData();

    const buttonParams: Pick<ButtonProps, 'size' | 'color' | 'variables'> = {
        size: isMobile ? 16 : 24,
        color: 'grey',
        variables: {
            buttonRadius: HOVER_BUTTON_BORDER_RADIUS,
            buttonPadding: pxToRem(
                isMobile ? HOVER_BUTTON_PADDING_MOBILE : HOVER_BUTTON_PADDING,
            ),
            iconPadding: 0,
        },
    };

    const { isInDesignBoard } = useHandleFollow();

    const canDownload = !isMobile && hasMarketingView;
    const pinterestShareUrl = isHydrated
        ? buildPinterestShareUrl({
              pageUrl: window.location.href,
              mediaUrl: imageURL,
              description: document.title,
          })
        : undefined;

    return (
        <Flex
            className={`${css(
                S.HoverButtonsImageContainer,
            )} hover-buttons-image`}
            vAlign="start"
        >
            {taggingEnabled && (
                <HoverAction
                    label="Start tagging"
                    action={
                        <Button
                            icon={<PriceTag3Line />}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSearchParams(
                                    {
                                        ...searchParams,
                                        tagging: 'true',
                                    },
                                    {
                                        replace: false,
                                        preventScrollReset: true,
                                    },
                                );

                                dispatchProductTagging({
                                    type: 'TOGGLE_TAGGING',
                                });
                            }}
                            {...buttonParams}
                        />
                    }
                />
            )}
            {pinterestShareUrl && (
                <HoverAction
                    label="Save to Pinterest"
                    action={
                        <Button
                            as="a"
                            icon={<PinterestFill />}
                            href={pinterestShareUrl}
                            target="_blank"
                            rel="noreferrer"
                            {...buttonParams}
                            onClick={(e) => {
                                e.stopPropagation();
                                void tracker.log('PinterestShare', {
                                    url: new URL(window.location.href),
                                    label: `Pinterest:${document.title}`,
                                    data: {
                                        ProfessionalID:
                                            professionalData?.professional.ID ??
                                            0,
                                        LibraryImageID: photoID,
                                        Source: 'HoverButtonsImage mobile',
                                    },
                                });
                            }}
                            className="text-black"
                        />
                    }
                />
            )}
            <HoverAction
                label={BOARD_TERMS.saveTo}
                action={
                    <FollowSelector
                        trigger={
                            <Button
                                icon={
                                    isInDesignBoard(photoID, 'PhotoPin') ? (
                                        <HeartFill />
                                    ) : (
                                        <HeartLine />
                                    )
                                }
                                {...buttonParams}
                            />
                        }
                        pinId={photoID}
                        libraryItemId={libraryItemId}
                        pinType="PhotoPin"
                    />
                }
            />
            {canDownload && downloadLink && (
                <HoverAction
                    label={'Download'}
                    action={
                        <Button
                            as="a"
                            icon={<Download2Line />}
                            href={encodeDownloadLink(downloadLink)}
                            download
                            {...buttonParams}
                            onClick={(e) => {
                                e.stopPropagation();
                                tracker.log('StaffDownloadImage', {
                                    url: new URL(window.location.href),
                                    targetTracker: 'archiproTracker',
                                    data: {
                                        // ImageID
                                        ExtraData: photoID,
                                    },
                                });
                            }}
                        />
                    }
                />
            )}
        </Flex>
    );
};

export default HoverButtonsImage;
